import { ActionButton, ActionLink, ArrowPopover, BodyText, DataField, SubsectionTitle, useMenu } from '@if/mui'
import { Badge, Box, Divider, Stack, styled, type PopoverProps } from '@mui/material'
import { Mail, MessageSquare, Phone } from 'lucide-react'
import { useTranslation } from 'react-i18next'

// The app bar sits on the navy `brand.chrome` in BOTH light and dark, so these
// header buttons are styled identically in both schemes (matching the mockup's
// header) instead of following the light/dark palette — a brand-colored button
// would otherwise collide with the navy bar (navy-on-navy in light).

// Primary header action: white fill, brand-navy label. Built on ActionButton
// (raw MUI Button is eslint-banned) with a contained base we recolor.
const ChromeFilledButton = styled(ActionButton)(({ theme }) => ({
  backgroundColor: theme.vars.palette.common.white,
  color: theme.brand.chrome,
  boxShadow: theme.shadows[1],
  '&:hover': { backgroundColor: theme.vars.palette.grey[100], boxShadow: theme.shadows[2] },
}))

// Secondary header action: transparent with a white outline + white label.
const ChromeOutlinedButton = styled(ActionButton)(({ theme }) => ({
  color: theme.vars.palette.common.white,
  backgroundColor: 'transparent',
  borderColor: 'rgba(255, 255, 255, 0.7)',
  '&:hover': {
    borderColor: theme.vars.palette.common.white,
    backgroundColor: 'rgba(255, 255, 255, 0.08)',
  },
}))

interface Props {
  /** Whether the Messages shortcut is shown (mirrors the messaging permission). */
  showMessages: boolean
  /** In-app route the Messages button navigates to. */
  messagesHref: string
  /** Unread chat entries; drives the Messages badge (hidden when 0). */
  unreadCount: number
  /** Per-company support phone (as displayed, e.g. "1-888-689-9876"). */
  phone: string
  /** Per-company support email. */
  email: string
  /** Per-company FAQ URL, already resolved for the current UI language; omitted when the company has none. */
  faqUrl?: string
}

/**
 * The app-bar end slot: a Messages shortcut and a "Need Help?" button whose
 * arrow callout carries the support phone, email and FAQ link — built from the
 * shared @if/mui primitives (ArrowPopover / DataField / ActionLink) rather than
 * hand-rolled markup.
 */
const AppBarSupport = ({ showMessages, messagesHref, unreadCount, phone, email, faqUrl }: Props) => {
  const { t } = useTranslation()
  const [buttonProps, popoverProps] = useMenu()

  // Strip formatting so the tel: scheme dials cleanly (same rule the app uses elsewhere).
  const telHref = `tel:${phone.replaceAll(/[- ()]/g, '')}`

  // ArrowPopover centres the callout on the button by default; override the origins
  // to hang it down-and-right from the button's left edge (like the mockup). Its
  // arrow logic still measures onto the button, landing the arrow near the left.
  const leftAligned: Partial<PopoverProps> = {
    anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
    transformOrigin: { vertical: 'top', horizontal: 'left' },
  }

  return (
    <>
      {showMessages && (
        <Badge badgeContent={unreadCount || undefined} color="error" overlap="rectangular">
          <ChromeFilledButton emphasis="principal" startIcon={MessageSquare} href={messagesHref}>
            {t('common.messages')}
          </ChromeFilledButton>
        </Badge>
      )}

      <ChromeOutlinedButton emphasis="normal" {...buttonProps}>
        {t('common.needHelp')}
      </ChromeOutlinedButton>

      <ArrowPopover {...popoverProps} {...leftAligned}>
        <Box sx={{ width: 288, px: 2, py: 1 }}>
          <Stack spacing={0.5} sx={{ pt: 1, pb: 1.5 }}>
            <SubsectionTitle>{t('common.needHelp')}</SubsectionTitle>
            <BodyText>{t('common.needHelpDescription')}</BodyText>
          </Stack>

          <Divider />

          <Stack spacing={0.5} sx={{ py: 1 }}>
            <DataField icon={Phone} iconVariant="tile" label={t('common.phone')} value={phone} href={telHref} />
            <DataField
              icon={Mail}
              iconVariant="tile"
              label={t('common.email')}
              value={email}
              href={`mailto:${email}`}
            />
          </Stack>

          {faqUrl && (
            <>
              <Divider />

              <Box sx={{ pt: 1 }}>
                <ActionLink href={faqUrl} target="_blank">
                  {t('common.viewFaqs')}
                </ActionLink>
              </Box>
            </>
          )}
        </Box>
      </ArrowPopover>
    </>
  )
}

export default AppBarSupport
